import { MemoryStorage } from './memory-storage'
import { WRITE_LOCK_NAME } from './shared-state'

/**
 * Everything the store needs from the browser, so tests can run several "tabs" in one process:
 * each tab has its own `session` storage and `tabId`, and all share one `local` storage.
 *
 * In the app the browser's own storage holds exactly two things: the signed-in user's id (`session`) and the activity history
 * (`history`). The shared data (`local`) is an in-memory store, so nothing else is ever written to the browser.
 */
export interface SyncContext {
  local: Storage
  session: Storage
  /** Where the activity history is kept between visits. `null` keeps none. */
  history: Storage | null
  /** Unique per tab; used to ignore our own broadcasts. */
  tabId: string
  channel: BroadcastChannel | null
  locks: LockManager | null
  /** Serializes writers within this tab when Web Locks is unavailable. */
  queue: { tail: Promise<unknown> }
}

export function createSyncContext(overrides: Partial<SyncContext> = {}): SyncContext {
  return {
    local: overrides.local ?? new MemoryStorage(),
    session: overrides.session ?? window.localStorage,
    history: overrides.history !== undefined ? overrides.history : window.localStorage,
    tabId: overrides.tabId ?? crypto.randomUUID(),
    channel: overrides.channel !== undefined ? overrides.channel : null,
    locks: overrides.locks !== undefined ? overrides.locks : typeof navigator !== 'undefined' && navigator.locks ? navigator.locks : null,
    queue: { tail: Promise.resolve() },
  }
}

/**
 * Runs `fn` as the only writer. Uses the Web Locks API, which serializes across tabs, so two tabs can't both
 * read revision N and both write N+1.
 *
 * Conflict strategy where Web Locks is missing: writers are queued within the tab and each one re-reads storage
 * immediately before writing. Two tabs writing in the same few milliseconds could still overwrite each other; this
 * is a documented limit of the fallback, and fine for a demo. BroadcastChannel is a notifier, not a transaction log.
 */
export function withWriteLock<T>(ctx: SyncContext, fn: () => T | Promise<T>): Promise<T> {
  if (ctx.locks) return ctx.locks.request(WRITE_LOCK_NAME, async () => fn())
  const run = ctx.queue.tail.then(fn, fn)
  ctx.queue.tail = run.catch(() => undefined)
  return run
}
